import { find, findIndex, get, isEmpty, pull, uniq, uniqBy } from 'lodash';

import { SELECT_CALL, SET_SELECTED_CALL } from '../actions/calls';
import {
  SELECT_SEARCH_CLIENT,
  SELECT_SEARCH_COMMUNICATION,
  SELECT_SEARCH_ORDER,
} from '../actions/search';
import {
  SELECT_TICKET,
  SET_TICKET_CLIENT,
  CREATE_TICKET_SUCCESS,
} from '../actions/tickets';
import {
  DELETE_NOTE_SUCCESS,
  DELETE_REASON_SUCCESS,
  EDIT_NOTE_SUCCESS,
  FETCH_LOGS_BY_CLIENT,
  FETCH_LOGS_BY_TICKET,
  FETCH_REASONS_SUCCESS,
  FETCH_SEGMENTS_SUCCESS,
  PIN_NOTE_SUCCESS,
  FLAG_NOTE_SUCCESS,
  SET_FILTERS,
  SET_RELATION,
  SET_LOGS,
  SET_LOGS_META,
  SET_PAGE,
  SUBMIT_REASON_SUCCESS,
  UPDATE_LOG,
  ADD_LOG_ELEMENT_TO_TICKET_LOGS,
  TOGGLE_TICKET_LOGS_GROUP,
  DELETE_LOG_ATTACHMENT_SUCCESS,
  UPDATE_TICKET_LOG_BY_WS,
} from '../actions/log';

import { TYPES as PERSON_TYPES } from '../../consts/persons';

import {
  LOG_RELATIONS,
  CLIENT_LOG_TYPES,
  TICKET_LOG_TYPES,
} from '../../consts/logs';

const initialLogsMeta = {
  page: 1,
  size: 25,
  total: 0,
  totalPages: 1,
};

const emptyLogState = {
  filters: {
    page: 1,
    size: 50,
    types: [],
  },
  total: 0,
  logs: [],
  availableTypes: [],
  fetchedForClientId: null,
  logsMeta: initialLogsMeta,
};

const initialState = {
  ...emptyLogState,
  activeClientId: null,
  activeTicketId: null,
  isNextPageLoading: false,
  isSegmentAllowed: false,
  reasons: null,
  requireNoteIds: [],
  segments: null,
  relateTo: LOG_RELATIONS.CLIENT,
};

const sortLogsByPin = list =>
  list.sort((log1, log2) => {
    const note1 = log1.note || log1;
    const note2 = log2.note || log2;

    if (!note1 && !note2) {
      return 0;
    }

    if (note1 && note2) {
      if (
        (!note1.isPinned && !note2.isPinned) ||
        (note1.isPinned && note2.isPinned)
      ) {
        return log2.time - log1.time;
      }

      if (note1.isPinned) {
        return -1;
      }

      if (note2.isPinned) {
        return 1;
      }
    }

    if (note1 && note1.isPinned) {
      return -1;
    }

    if (note2 && note2.isPinned) {
      return 1;
    }

    return log2.time - log1.time;
  });

export default (state = initialState, { payload, type }) => {
  switch (type) {
    case FETCH_LOGS_BY_CLIENT: {
      const personId = get(payload, 'id');
      const personType = get(payload, 'type');
      const isClient = personType === PERSON_TYPES.CLIENT;
      const activeClientId = isClient && personId;

      return {
        ...state,
        ...emptyLogState,
        activeClientId,
        isNextPageLoading: isClient,
      };
    }

    case FETCH_LOGS_BY_TICKET: {
      const ticketId = get(payload, 'id');

      return {
        ...state,
        ...emptyLogState,
        activeTicketId: ticketId,
        isNextPageLoading: true,
      };
    }

    case SET_SELECTED_CALL: {
      const personId = get(payload, 'contact.id');
      const personType = get(payload, 'contact.type');
      const isClient = personType === PERSON_TYPES.CLIENT;
      const activeClientId = isClient && personId;

      return {
        ...state,
        ...emptyLogState,
        activeClientId,
        isNextPageLoading: isClient,
        relateTo: LOG_RELATIONS.CLIENT,
      };
    }

    case SET_TICKET_CLIENT: {
      const personId = get(payload, 'client.id');
      const personType = get(payload, 'client.type');
      const isClient = personType === PERSON_TYPES.CLIENT;
      const activeClientId = isClient && personId;

      return {
        ...state,
        ...emptyLogState,
        activeClientId,
        isNextPageLoading: isClient,
        relateTo: LOG_RELATIONS.TICKET,
      };
    }

    case CREATE_TICKET_SUCCESS: {
      const activeTicketId = get(payload, 'ticket.id');
      return {
        ...state,
        ...emptyLogState,
        activeClientId: null,
        activeTicketId,
        isNextPageLoading: true,
        relateTo: LOG_RELATIONS.TICKET,
      };
    }

    case SELECT_TICKET: {
      const activeTicketId = get(payload, 'id');
      return {
        ...state,
        ...emptyLogState,
        activeClientId: null,
        activeTicketId,
        isNextPageLoading: true,
        relateTo: LOG_RELATIONS.TICKET,
      };
    }

    case SELECT_SEARCH_COMMUNICATION: {
      const activeTicketId = get(payload, 'ticketId');
      return {
        ...state,
        ...emptyLogState,
        activeClientId: null,
        activeTicketId,
        isNextPageLoading: true,
        relateTo: LOG_RELATIONS.TICKET,
      };
    }

    case SELECT_SEARCH_CLIENT:
    case SELECT_SEARCH_ORDER: {
      const activeClientId = get(payload, 'clientId');
      return {
        ...state,
        ...emptyLogState,
        activeClientId,
        activeTicketId: null,
        isNextPageLoading: true,
        relateTo: LOG_RELATIONS.TICKET,
      };
    }

    case SELECT_CALL: {
      return {
        ...state,
        ...emptyLogState,
        activeClientId: null,
        activeTicketId: null,
        isNextPageLoading: true,
        relateTo: LOG_RELATIONS.CLIENT,
      };
    }

    case SET_FILTERS: {
      const { filters } = payload;
      const { availableTypes: prevAvailableTypes } = state;
      return {
        ...state,
        ...emptyLogState,
        filters: { ...state.filters, ...filters, page: 1 },
        availableTypes: prevAvailableTypes,
        isNextPageLoading: true,
        logsMeta: initialLogsMeta,
      };
    }

    case SET_RELATION: {
      const { relateTo } = payload;
      return {
        ...state,
        ...emptyLogState,
        logsMeta: initialLogsMeta,
        relateTo,
        isNextPageLoading: true,
      };
    }

    case SET_LOGS: {
      const { clientId, logs, availableTypes, isNextPageLoading, total } =
        payload;
      const {
        logs: prevLogs,
        availableTypes: prevAvailableTypes,
        fetchedForClientId,
      } = state;
      const isSameClient = clientId === fetchedForClientId;

      const newLogs = isSameClient ? [...prevLogs, ...logs] : logs;

      return {
        ...state,
        fetchedForClientId: clientId,
        isNextPageLoading,
        logs: newLogs,
        ...(availableTypes && {
          availableTypes: isSameClient
            ? uniq([...prevAvailableTypes, ...availableTypes])
            : availableTypes,
        }),
        total,
      };
    }

    case SET_LOGS_META: {
      return {
        ...state,
        logsMeta: payload,
      };
    }

    case SET_PAGE: {
      const { page } = payload;
      return {
        ...state,
        filters: { ...state.filters, page },
        logsMeta: {
          ...state.logsMeta,
          page,
        },
        isNextPageLoading: true,
      };
    }

    case FETCH_REASONS_SUCCESS: {
      const { reasons, requireNoteIds } = payload;
      return {
        ...state,
        reasons,
        requireNoteIds,
      };
    }

    case FETCH_SEGMENTS_SUCCESS: {
      const { segments } = payload;
      return { ...state, segments };
    }

    case EDIT_NOTE_SUCCESS: {
      const { id, relateTo, value } = payload;
      const { logs: prevLogs } = state;

      if (relateTo === LOG_RELATIONS.CLIENT) {
        const editedLog = find(prevLogs, log => get(log, 'note.id') === id);
        editedLog.note.text = value;
      } else {
        const editedLog = find(prevLogs, log => log.id === id);
        editedLog.valueText = value;
      }

      return { ...state, logs: [...prevLogs] };
    }

    case DELETE_LOG_ATTACHMENT_SUCCESS: {
      const { noteId, attachId, relateTo } = payload;
      const { logs: prevLogs } = state;

      if (relateTo === LOG_RELATIONS.CLIENT) {
        const editedLog = find(prevLogs, log => get(log, 'note.id') === noteId);
        editedLog.note.attachments = editedLog.note.attachments.filter(
          attach => attach.id !== attachId
        );
      } else {
        const editedLog = find(prevLogs, log => log.id === noteId);
        editedLog.attachments = editedLog.attachments.filter(
          attach => attach.id !== attachId
        );
      }

      return { ...state, logs: [...prevLogs] };
    }

    case PIN_NOTE_SUCCESS: {
      const { id, relateTo, isPinned } = payload;
      const { logs: prevLogs } = state;

      if (relateTo === LOG_RELATIONS.CLIENT) {
        const pinnedLog = find(prevLogs, log => get(log, 'note.id') === id);
        pinnedLog.note.isPinned = isPinned;
      } else {
        const pinnedLog = find(prevLogs, log => log.id === id);
        pinnedLog.isPinned = isPinned;
      }

      const sortedLogs = sortLogsByPin(prevLogs);
      return { ...state, logs: [...sortedLogs] };
    }

    case FLAG_NOTE_SUCCESS: {
      const { id, isFlagged } = payload;
      const { logs: prevLogs } = state;

      const pinnedLog = find(prevLogs, log => log.id === id);
      pinnedLog.type = isFlagged
        ? TICKET_LOG_TYPES.TASK
        : TICKET_LOG_TYPES.NOTE;

      const sortedLogs = sortLogsByPin(prevLogs);
      return { ...state, logs: [...sortedLogs] };
    }

    case DELETE_NOTE_SUCCESS: {
      const { logs } = state;
      const { id } = payload;
      const index = findIndex(
        logs,
        communication => get(communication, 'note.id') === id
      );
      if (index === -1) {
        return state;
      }

      logs.splice(index, 1);
      return { ...state, logs: [...logs] };
    }

    case SUBMIT_REASON_SUCCESS: {
      const {
        callId,
        reasonIds: selectedReasonIds,
        reasonDescription,
        segmentId: selectedSegmentId,
      } = payload;
      const { logs: prevLogs } = state;

      const editedLog = find(prevLogs, log => get(log, 'call.id') === callId);
      editedLog.call = Object.assign(editedLog.call, {
        selectedReasonIds,
        reasonDescription,
        selectedSegmentId,
      });

      return { ...state, logs: [...prevLogs] };
    }

    case DELETE_REASON_SUCCESS: {
      const { callId, reasonId } = payload;
      const { logs: prevLogs } = state;

      const editedLog = find(prevLogs, log => get(log, 'call.id') === callId);
      const selectedReasonIds = get(editedLog, 'call.selectedReasonIds') || [];
      const reasonDescription =
        (!isEmpty(selectedReasonIds) &&
          get(editedLog, 'call.reasonDescription')) ||
        '';

      editedLog.call = Object.assign(editedLog.call, {
        selectedReasonIds: pull(selectedReasonIds, reasonId),
        reasonDescription,
      });
      return { ...state, logs: [...prevLogs] };
    }

    case UPDATE_LOG: {
      const { action, entity } = payload;
      const { logs } = state;
      const idPath =
        entity.type === CLIENT_LOG_TYPES.CALL ? 'call.id' : 'note.id';
      const logId = get(entity, idPath);
      const index = findIndex(logs, l => get(l, idPath) === logId);

      if (action === 'delete') {
        logs.splice(index, 1);
        return {
          ...state,
          logs: [...logs],
          total: Math.max(state.total - 1, 0),
        };
      } else if (index === -1) {
        // eslint-disable-line no-else-return
        return {
          ...state,
          logs: sortLogsByPin([entity, ...logs]),
          total: state.total + 1,
        };
      } else {
        logs.splice(index, 1, entity);
        return {
          ...state,
          logs: [...logs],
          total: state.total + 1,
        };
      }
    }

    case ADD_LOG_ELEMENT_TO_TICKET_LOGS: {
      const { relateTo, logs: prevLogs } = state;
      const { ticketLog } = payload;

      if (relateTo !== LOG_RELATIONS.TICKET) return state;

      const updatedLogs = sortLogsByPin([ticketLog, ...prevLogs]);

      return {
        ...state,
        logs: updatedLogs,
        total: state.total + 1,
      };
    }

    case TOGGLE_TICKET_LOGS_GROUP: {
      const { logs } = state;
      const { id } = payload;

      const groupIndex = logs.findIndex(({ groupId }) => groupId === id);

      const group = logs[groupIndex];
      const hiddenLogs = logs[groupIndex].groupItems;

      let updatedLogs = [...logs];

      if (group.isExpanded) {
        updatedLogs.splice(groupIndex + 1, hiddenLogs.length);
        group.isExpanded = false;
      } else {
        updatedLogs = [
          ...logs.slice(0, groupIndex + 1),
          ...hiddenLogs,
          ...logs.slice(groupIndex + 1),
        ];
        group.isExpanded = true;
      }

      return {
        ...state,
        logs: updatedLogs,
      };
    }

    case UPDATE_TICKET_LOG_BY_WS: {
      const { relateTo, logs: prevLogs } = state;
      const { ticketLog } = payload;
      const { id } = ticketLog || {};

      if (relateTo !== LOG_RELATIONS.TICKET || !id) {
        return state;
      }

      const logsToSearch = prevLogs.flatMap(log =>
        log.grouped ? (log.groupItems || []) : [log]
      );
      const editedLog = find(logsToSearch, log => log.id === id);

      if (!editedLog) {
        return state;
      }

      Object.assign(editedLog, ticketLog);

      return {
        ...state,
        logs: [...prevLogs],
      };
    }

    default:
      return state;
  }
};
